import { randomUUID } from './uuid.ts';
import { cancelReportTransfers } from './cancelTransfers';
import type { CompanionNotification, Geofence, LocalAttachment, LocalProfile, LocalReport, MatrixOutboxItem, MatrixRoom, OfflineOperation, PlanningItem, ProjectImageUpload, ReportDataSources, ReportOption, TimeEntry, TimeRuntime } from './types';

const DB_NAME = 'flownxt-companion';
const DB_VERSION = 1;
const STORE = 'records';

interface RecordValue<T = unknown> { key: string; value: T; updatedAt: string }

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'key' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function put<T>(key: string, value: T): Promise<void> {
  const db = await openDB();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put({ key, value, updatedAt: new Date().toISOString() } satisfies RecordValue<T>);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

export async function get<T>(key: string, fallback: T): Promise<T> {
  const db = await openDB();
  const result = await new Promise<RecordValue<T> | undefined>((resolve, reject) => {
    const request = db.transaction(STORE).objectStore(STORE).get(key);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  db.close();
  return result?.value ?? fallback;
}

export async function remove(key: string): Promise<void> {
  const db = await openDB();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

// Read and write within one transaction so a response cannot erase newly queued work.
export async function updateOperations(update: (items: OfflineOperation[]) => OfflineOperation[]): Promise<OfflineOperation[]> {
  const db = await openDB();
  try {
    return await new Promise<OfflineOperation[]>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const store = tx.objectStore(STORE);
      let next: OfflineOperation[] = [];
      const request = store.get('operations');
      request.onsuccess = () => {
        try {
          next = update(request.result?.value ?? []);
          store.put({ key: 'operations', value: next, updatedAt: new Date().toISOString() });
        } catch { tx.abort(); }
      };
      tx.oncomplete = () => resolve(next);
      tx.onerror = tx.onabort = () => reject(tx.error || new Error('Warteschlange konnte nicht gespeichert werden.'));
    });
  } finally { db.close(); }
}

export const cache = {
  profile: () => get<LocalProfile | null>('profile', null),
  setProfile: (value: LocalProfile) => put('profile', value),
  planning: () => get<PlanningItem[]>('planning', []),
  setPlanning: (value: PlanningItem[]) => put('planning', value),
  geofences: () => get<Geofence[]>('geofences', []),
  setGeofences: (value: Geofence[]) => put('geofences', value),
  timeEntries: () => get<TimeEntry[]>('time_entries', []),
  setTimeEntries: (value: TimeEntry[]) => put('time_entries', value),
  timeRuntime: () => get<TimeRuntime>('time_runtime', { status: 'ready' }),
  setTimeRuntime: (value: TimeRuntime) => put('time_runtime', value),
  operations: () => get<OfflineOperation[]>('operations', []),
  setOperations: (value: OfflineOperation[]) => put('operations', value),
  reports: () => get<LocalReport[]>('reports', []),
  setReports: (value: LocalReport[]) => put('reports', value),
  reportOptions: () => get<ReportOption[]>('report_options', []),
  setReportOptions: (value: ReportOption[]) => put('report_options', value),
  reportDataSources: () => get<ReportDataSources>('report_data_sources', {}),
  setReportDataSources: (value: ReportDataSources) => put('report_data_sources', value),
  notifications: () => get<CompanionNotification[]>('notifications', []),
  setNotifications: (value: CompanionNotification[]) => put('notifications', value),
  attachments: () => get<LocalAttachment[]>('attachments', []),
  setAttachments: (value: LocalAttachment[]) => put('attachments', value),
  matrixRooms: () => get<MatrixRoom[]>('matrix_rooms', []),
  setMatrixRooms: (value: MatrixRoom[]) => put('matrix_rooms', value),
  matrixOutbox: () => get<MatrixOutboxItem[]>('matrix_outbox', []),
  setMatrixOutbox: (value: MatrixOutboxItem[]) => put('matrix_outbox', value),
  matrixSince: () => get<string>('matrix_since', ''),
  setMatrixSince: (value: string) => put('matrix_since', value),
  projectImageUploads: () => get<ProjectImageUpload[]>('project_image_uploads', []),
  setProjectImageUploads: (value: ProjectImageUpload[]) => put('project_image_uploads', value),
};

export async function clearProfileData(): Promise<void> {
  const db = await openDB();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const request = tx.objectStore(STORE).openCursor();
      request.onsuccess = () => { const cursor = request.result; if(cursor) {if(String(cursor.key).startsWith('cancelled_report_transfers:'))cursor.delete();cursor.continue();} };
      tx.oncomplete = () => resolve(); tx.onerror = tx.onabort = () => reject(tx.error);
    });
  } finally {db.close();}

  for (const key of ['absence_history', 'time_history', 'profile', 'planning', 'geofences', 'time_entries', 'time_runtime', 'operations', 'reports', 'report_options', 'report_data_sources', 'notifications', 'attachments', 'matrix_rooms', 'matrix_outbox', 'matrix_since', 'project_image_uploads']) await remove(key);
}

export async function storageEstimate(): Promise<{ usage: number; quota: number }> {
  if (!navigator.storage?.estimate) return { usage: 0, quota: 0 };
  const value = await navigator.storage.estimate();
  return { usage: value.usage ?? 0, quota: value.quota ?? 0 };
}

// All queue changes and the recovery copy commit together, including attachment blobs.
export async function cancelFailedReportTransfers(ids: string[]) {
  const db = await openDB();
  try {
    return await new Promise<ReturnType<typeof cancelReportTransfers>>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const store = tx.objectStore(STORE);
      const requests = ['operations', 'reports', 'attachments'].map(key => store.get(key));
      let result: ReturnType<typeof cancelReportTransfers>;
      let failure: unknown;
      requests[2].onsuccess = () => {
        try {
          result = cancelReportTransfers(requests[0].result?.value ?? [], requests[1].result?.value ?? [], requests[2].result?.value ?? [], ids);
          const updatedAt = new Date().toISOString();
          store.put({key: `cancelled_report_transfers:${randomUUID()}`, value: result.archive, updatedAt});
          for (const key of ['operations', 'reports', 'attachments'] as const) store.put({key, value: result[key], updatedAt});
        } catch (reason) {failure=reason;tx.abort();}
      };
      tx.oncomplete = () => resolve(result);
      tx.onerror = tx.onabort = () => reject(failure || tx.error || new Error('Übertragungen konnten nicht verworfen werden.'));
    });
  } finally {db.close();}
}
